import { useState } from "react";

import { useTranslation } from "react-i18next";

import { useForecast } from "@/api/demandForecast";
import type { PlanningConfig } from "@/api/planningConfig";

import { CleansingDrawer } from "./CleansingDrawer";
import { ForecastingSection } from "./ForecastingSection";
import { PlanningPolicySection } from "./PlanningPolicySection";

interface PlanningConfigPanelProps {
  itemId: number;
  locationId: number;
  /** null = no demand history yet at this location, so nothing to forecast. */
  config: PlanningConfig | null;
}

/** Right rail for a node: forecast settings, then the replenishment policy. */
export default function PlanningConfigPanel({
  itemId,
  locationId,
  config,
}: PlanningConfigPanelProps) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col divide-y">
      <div className="pb-4">
        {config ? (
          <Forecasting
            itemId={itemId}
            locationId={locationId}
            config={config}
          />
        ) : (
          <>
            <div className="text-muted-foreground mb-2 text-xs font-semibold tracking-wider uppercase">
              {t("items.detail.forecasting.title")}
            </div>
            <p className="text-muted-foreground text-xs">
              {t("items.detail.forecasting.noHistory")}
            </p>
          </>
        )}
      </div>
      <div className="pt-4">
        <PlanningPolicySection
          itemId={itemId}
          locationId={locationId}
          config={config}
        />
      </div>
    </div>
  );
}

function Forecasting({
  itemId,
  locationId,
  config,
}: PlanningConfigPanelProps & { config: PlanningConfig }) {
  const forecastQuery = useForecast(itemId, [locationId]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  return (
    <>
      <ForecastingSection
        config={config}
        excludedCount={forecastQuery.data?.excludedCount ?? 0}
        onReview={() => setDrawerOpen(true)}
      />
      <CleansingDrawer
        item={itemId}
        location={locationId}
        open={drawerOpen}
        onClose={setDrawerOpen}
      />
    </>
  );
}
